import { mergeMap, withLatestFrom } from 'rxjs/operators';
import { EMPTY } from 'rxjs';
import { IEpic, isTypeOfAndGetPayload } from '@cbridge-utils/redux-observable';
import { IGetSelectorStateType } from '@cbridge-utils/reselect';
import { MAX_PAGE_SIZE } from '@cbridge-utils/datagrid';
import { catchApiError } from '@cbridge-packages/api-base';
import { INotificationApiDependencySegment } from '@cbridge-packages/api-notifications';
import { createApiErrorNotification } from '@cbridge-packages/notification';
import { AlertTranslator } from '@cbridge-packages/alert';
import { AlertsDashboardAction } from '../alertsDashboard.action';
import { AlertsDashboardTranslator } from '../alertsDashboard.translator';
import { alertsDashboardStoreSelector } from '../alertsDashboard.selector';

type IState = IGetSelectorStateType<typeof alertsDashboardStoreSelector>;
type IDependencies = INotificationApiDependencySegment;

export const loadAlertListEpic: IEpic<IState, IDependencies> = (action$, state$, dependencies) => action$.pipe(
  isTypeOfAndGetPayload(AlertsDashboardAction.loadAlertList.started),
  mergeMap((params) => {
    const { resolve, reject, ...payload } = params;
    return dependencies.nsApi.getTriggeredAlerts(AlertsDashboardTranslator.toApiPayload(payload)).pipe(
      withLatestFrom(state$),
      mergeMap(([response, state]) => {
        // Drop a response whose alert type no longer matches the current filter — e.g.
        // the unfiltered mount fetch (or a previous type) resolving after the filter
        // changed. Applying it would repaint stale rows under the new filter and let the
        // "No rows" overlay sit over them (LB-4003 bug 4).
        if (alertsDashboardStoreSelector(state).filter.type !== payload.type) {
          return EMPTY;
        }

        const result = response.triggeredAlerts.map(AlertTranslator.fromApi);
        const limit = payload.limit ?? MAX_PAGE_SIZE;
        const isLastPage = result.length < limit;
        // Absolute end of the loaded range — idempotent when the grid re-fetches
        // the same offset (revalidate / polling / filter change), so rowCount can't
        // inflate and trigger an endless skeleton loop (LB-4003).
        const rowCount = isLastPage
          ? (payload.skip ?? 0) + result.length
          : -1;

        resolve?.({ rows: result, rowCount });

        return [
          AlertsDashboardAction.loadAlertList.done({ params, result }),
        ];
      }),
      catchApiError((error) => {
        reject?.(error?.description);
        return [
          AlertsDashboardAction.loadAlertList.failed({ params, error }),
          createApiErrorNotification(error),
        ];
      }),
    );
  }),
);
